/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { LegendItemData } from '#utils/UNSAFE_visTypes/legend';
import { Font, TextDimensions, useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { getGridNums } from '#utils/PRIVATE_legendUtils/legendUtils';

type Props<K> = {
  width: number;
  getTextDimensions?: ((textString: string, font?: Font | undefined) => TextDimensions) | undefined;
  resolvedVars: Record<string, any>;
  orientation?: 'vertical' | 'horizontal';
  legendFontStylesObj?: Font;
  symbolWidth?: number;
  symbolHeight?: number;
  items: LegendItemData<K>[];
};
export type LayoutInfo = {
  totalWidth: number;
  maxItemWidth: number;
  maxItemHeight: number;
  itemsPerRow: number;
  isWrapped?: boolean;
};
type getTextDimensionsType = NonNullable<
  NonNullable<ReturnType<typeof useTextDimensions>>['getTextDimensions']
>;

export const getLinearLegendLayoutInfo = <K>({
  width,
  getTextDimensions,
  resolvedVars,
  orientation,
  legendFontStylesObj = {},
  symbolWidth,
  items
}: Props<K>) => {
  let maxWidth = 0;
  let maxHeight = 0;
  let totalWidth = 0;
  let isWrapped = false;

  const containerWidth = width;

  if (!Object.keys(resolvedVars).length || !getTextDimensions) {
    return;
  }

  const unitLength = parseFloat(resolvedVars['unit']);

  const { maxItemWidth, maxItemHeight, horizontalSectionWidth } = getMaxWidth(
    items,
    unitLength,
    getTextDimensions,
    legendFontStylesObj,
    symbolWidth
  );
  totalWidth = horizontalSectionWidth;
  maxWidth = maxItemWidth;
  maxHeight = maxItemHeight;
  isWrapped = horizontalSectionWidth > containerWidth!;

  const legendLayoutInfo = {
    totalWidth,
    maxItemWidth: Math.min(maxWidth, containerWidth!),
    maxItemHeight: maxHeight,
    itemsPerRow:
      orientation === 'vertical' ? 1 : Math.max(1, Math.floor(containerWidth / maxWidth)),
    isWrapped
  };

  return legendLayoutInfo;
};

/**
 * Returns maximum of the width among the items in a section of legend also return the width of the entire section.
 * @param items Legend items array
 * @param unitLength Base  element
 * @param getTextDimensions Text width measurement function
 * @param legendFontStylesObj Legend text font style properties object
 * @param symbolWidth Width of legend symbol
 *
 * @returns
 */
const getMaxWidth = <K>(
  items: LegendItemData<K>[],
  unitLength: number,
  getTextDimensions: getTextDimensionsType,
  legendFontStylesObj?: Font,
  symbolWidth?: number,
  symbolHeight?: number
) => {
  let maxItemWidth = 0;
  let maxItemHeight = 0;
  let horizontalSectionWidth = 0;
  let itemSymbolWidth = 0;
  let itemSymbolHeight = 0;
  if (symbolWidth) {
    itemSymbolWidth = unitLength * 1.5 + symbolWidth;
  } else {
    //itemSymbolWidth = marginLeft + marginRight + width below expression is simplified
    itemSymbolWidth = 4 * unitLength;
  }

  if (symbolHeight) {
    // symbolHeight/defaultSymbolHeight + symbolPaddingTopBottom
    itemSymbolHeight = symbolHeight + unitLength * 1.7;
  } else {
    // symbolHeight/defaultSymbolHeight + symbolPaddingTopBottom
    itemSymbolHeight = 2.5 * unitLength + unitLength * 1.7;
  }

  items?.forEach((item) => {
    const legendItem = item as LegendItemData<K>;
    const { width, height } = getTextDimensions(legendItem.text, legendFontStylesObj);
    //itemTextWidth = paddingLeft + paddingRight + width
    const itemTextWidth = unitLength * 2.5 + width;
    //horizontalSectionWidth = total width of the section
    horizontalSectionWidth += itemSymbolWidth + itemTextWidth;
    maxItemWidth = Math.max(maxItemWidth, itemSymbolWidth + itemTextWidth);
    maxItemHeight = Math.max(maxItemHeight, height, itemSymbolHeight);
  });

  return { maxItemWidth, maxItemHeight, horizontalSectionWidth };
};

export function _getPreferredSize<K>(
  _width: number,
  _height: number,
  items: LegendItemData<K>[],
  resolvedVars: Record<string, any>,
  orientation: 'vertical' | 'horizontal',
  legendFontStylesObj: Record<string, any>,
  symbolWidth?: number,
  symbolHeight?: number,
  getTextDimensions?: (textString: string, font?: Font | undefined) => TextDimensions
) {
  if (!getTextDimensions) {
    return undefined;
  }
  const layoutInfo = getLinearLegendLayoutInfo({
    width: _width,
    getTextDimensions,
    resolvedVars,
    orientation,
    legendFontStylesObj,
    symbolWidth: symbolWidth,
    symbolHeight: symbolHeight,
    items
  });
  const { itemsPerCol, itemsPerRow } = getGridNums(items.length, layoutInfo, orientation);
  const unitLength = parseFloat(resolvedVars['unit']);
  const preferredWidth = Math.min(
    _width,
    itemsPerCol === 1
      ? layoutInfo!.totalWidth
      : itemsPerRow
      ? layoutInfo!.maxItemWidth * itemsPerRow!
      : layoutInfo!.totalWidth
  );
  const paddingLegendItems = 0.8 * unitLength;
  const paddingLegendTopBottom = 1.5 * unitLength;

  const legendHeight = itemsPerCol
    ? layoutInfo!.maxItemHeight * itemsPerCol! +
      (itemsPerCol! - 1) * paddingLegendItems +
      paddingLegendTopBottom
    : layoutInfo!.maxItemHeight + paddingLegendTopBottom;
  const preferredHeight = Math.min(_height, legendHeight);

  return { width: Math.ceil(preferredWidth), height: Math.ceil(preferredHeight) };
}
